/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import PropTypes from 'prop-types';

import { Button, PageGridContainer, Typography2, breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { SECONDARY_CONSUMER_REBRAND_IMG_SRC } from 'bundles/front-page/constants/defaults';
import { handleConsumerCTAClick } from 'bundles/front-page/utils';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';

import _t from 'i18n!nls/front-page';

const AUTH_MODE = 'signup';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const styles = {
  showBackground: css`
    background-color: #f9f7ef;
  `,
  secondaryConsumerCtaRebrandSection: css`
    overflow: hidden;
    padding: 48px 0;
    margin: 32px 0;

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300) 0;
      margin: 0;
    }
  `,
  secondaryConsumerSectionContainer: css`
    display: flex;
    padding: var(--cds-spacing-400) var(--cds-spacing-200);

    @media screen and (max-width: 1024px) {
      flex-direction: column;
      padding: 0 15px;
    }

    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-200);
    }
  `,
  imageContainer: css`
    margin-right: 95px;
    margin-top: -41px;

    @media screen and (max-width: 1024px) {
      margin: 0 0 38px 38px;
    }

    @media (max-width: 600px) {
      margin-left: 0;
    }

    @media (max-width: 510px) {
      margin-left: -28px;
    }

    @media (max-width: 480px) {
      margin-left: -45px;
      margin-bottom: 0;
    }

    @media (max-width: 375px) {
      margin-left: -39px;
    }

    @media (max-width: 320px) {
      margin-left: -35px;
    }

    ${breakpoints.down('xs')} {
      padding-bottom: var(--cds-spacing-200);
      margin: 0 auto;
    }
  `,
  imageWrapper: css`
    max-height: 330px;
    max-width: 471px;
    width: 100%;
  `,
  image: css`
    @media (max-width: 480px) {
      width: 100%;
    }
  `,
  contentContainer: css`
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media screen and (max-width: 1024px) {
      max-width: 75%;
      margin-left: 98px;
    }

    @media (max-width: 600px) {
      margin-left: 58px;
    }

    @media (max-width: 510px) {
      margin-left: 28px;
    }

    @media (max-width: 480px) {
      max-width: 100%;
      margin: 0;
      padding: 0;
    }

    ${breakpoints.down('xs')} {
      max-width: 100%;
      margin: var(--cds-spacing-50);
    }
  `,
  contentHeader: css`
    color: #1f1f1f;
    margin: 0;
    margin-bottom: 24px;
    max-width: 75%;
    font-size: var(--cds-font-size-display2-lg);

    @media screen and (max-width: 1024px) {
      max-width: 100%;
    }

    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-150);
      max-width: 100%;
    }
  `,
  contentSubtitle: css`
    color: #1f1f1f;
    margin-bottom: 1.5rem;
    max-width: 80%;

    @media screen and (max-width: 1024px) {
      margin-bottom: 38px;
      max-width: 85%;
    }

    @media screen and (max-width: 480px) {
      max-width: 95%;
    }

    ${breakpoints.down('xs')} {
      max-width: 100%;
      margin-bottom: var(--cds-spacing-300);
    }
  `,
  joinForFree: css`
    border-radius: 4px;
    border: 0;
    background-image: none;
    color: white;
    font-size: 16px;
    font-weight: bold;
    letter-spacing: -0.1px;
    line-height: 24px;
    width: 152px;
    height: 48px;
    padding: 0;

    @media screen and (max-width: 480px) {
      max-width: 100%;
      width: 100%;
    }

    ${breakpoints.down('xs')} {
      max-width: 100%;
      width: 100%;

      > span {
        ${typography2.actionPrimary}
        padding: var(--cds-spacing-100) 0;
      }
    }
  `,
};

type Props = {
  showBackground?: boolean;
};

const SecondaryConsumerCta = ({ showBackground = true }: Props, { router }: $TSFixMe) => {
  const isMobileDeviceWidth = useMediaQuery(breakpoints.down('xs'));
  const trackEvent = useTracker();
  const handleOnClick = () => {
    trackEvent('click_button', {
      button: { name: 'signup' },
      pageSection: { sectionName: 'secondary_consumer_cta' },
    });
    handleConsumerCTAClick(router, AUTH_MODE);
  };
  return (
    <section
      className="rc-SecondaryConsumerCta"
      css={[styles.secondaryConsumerCtaRebrandSection, showBackground ? styles.showBackground : '']}
    >
      <div className="bt3-container" css={styles.secondaryConsumerSectionContainer}>
        <div css={styles.imageContainer}>
          <div css={styles.imageWrapper}>
            <LazyImgix
              css={styles.image}
              src={SECONDARY_CONSUMER_REBRAND_IMG_SRC}
              maxHeight={330}
              maxWidth={471}
              alt={_t('A lady standing in front of a meeting table')}
            />
          </div>
        </div>

        <div css={styles.contentContainer}>
          <Typography2
            component="h2"
            variant={isMobileDeviceWidth ? 'titleSmall' : 'titleLarge'}
            css={styles.contentHeader}
          >
            {_t('Take the next step toward your personal and professional goals with Coursera.')}
          </Typography2>

          <Typography2 component="p" variant="bodyPrimary" css={styles.contentSubtitle}>
            {_t('Join now to receive personalized recommendations from the full Coursera catalog.')}
          </Typography2>

          <TrackedButton
            css={styles.joinForFree}
            trackingName="secondary_consumer_join_for_free"
            onClick={handleOnClick}
            variant="primary"
            size="small"
          >
            {_t('Join for Free')}
          </TrackedButton>
        </div>
      </div>
    </section>
  );
};

SecondaryConsumerCta.contextTypes = {
  router: PropTypes.object,
};

export default SecondaryConsumerCta;
